import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";

const Pagination = ({
  nextTokens = null,
  currentPage,
  isLoading,
  onPageChange,
  perPage,
  onPerPageChange,
  totalPages: providedTotalPages = 1,
  items = [],
  isOffline = false,
}) => {
  const { t } = useTranslation();

  // Calculate total pages based on the mode
  const totalPages = isOffline
    ? Math.ceil(items.length / perPage)
    : !nextTokens
    ? providedTotalPages
    : Object.entries(nextTokens).reduce((last, [page, token], i, arr) => {
        if (token === null && i > 0) {
          return Math.min(last, Number(page) - 1);
        }
        return last;
      }, Object.keys(nextTokens).length);

  // Generate available pages based on the mode
  const availablePages = isOffline
    ? Array.from({ length: totalPages }, (_, i) => i + 1)
    : !nextTokens
    ? Array.from({ length: totalPages }, (_, i) => i + 1)
    : Object.keys(nextTokens)
        .map(Number)
        .filter((page) => page <= totalPages);

  const getPageNumbers = () => {
    return availablePages
      .filter(
        (page) =>
          page === 1 ||
          page === totalPages ||
          (page >= currentPage - 1 && page <= currentPage + 1)
      )
      .reduce((acc, page, i, arr) => {
        if (i > 0 && arr[i] - arr[i - 1] > 1) {
          acc.push("...");
        }
        acc.push(page);
        return acc;
      }, []);
  };

  // Handle page change for offline pagination
  const handlePageChange = (newPage) => {
    if (isOffline) {
      const start = (newPage - 1) * perPage;
      const end = start + perPage;
      const pageItems = items.slice(start, end);
      onPageChange(newPage, pageItems);
    } else {
      onPageChange(newPage);
    }
  };

  // Handle per page change for offline pagination
  const handlePerPageChange = (newPerPage) => {
    if (isOffline) {
      const start = 0;
      const end = newPerPage;
      const pageItems = items.slice(start, end);
      onPerPageChange(newPerPage);
      onPageChange(1, pageItems);
    } else {
      onPerPageChange(newPerPage);
    }
  };

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      {/* Mobile */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {t("common.previous")}
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {t("common.next")}
        </button>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {perPage && onPerPageChange ? (
          <select
            value={perPage}
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            disabled={isLoading}
            className="rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        ) : (
          <div>
            <p className="text-sm text-gray-700">
              {t("common.page")}{" "}
              <span className="font-medium">{currentPage}</span>
            </p>
          </div>
        )}

        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span className="sr-only">{t("common.previous")}</span>
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            {getPageNumbers().map((pageNum, idx) =>
              pageNum === "..." ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                >
                  ...
                </span>
              ) : (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  disabled={isLoading}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    currentPage === pageNum
                      ? "z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  } disabled:opacity-50`}
                >
                  {pageNum}
                </button>
              )
            )}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span className="sr-only">{t("common.next")}</span>
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
